// FIN-13958 — FRAML View Create Case / Merge to Case act on MANY customers at once (one BE request
// per customer). Each request used to report through the shared cmConvertAlertsToCase /
// cmMergeAlertsToCase boolean flags, so the page toasted once per failed request — N customers,
// N "Already part of a case: …" pop-ups at once — and, because two failures could land before the
// page's [flag] effect ran (true → true), it could also silently DROP some of them.
//
// The FRAML saga now runs every per-customer request, folds the results with buildBulkCaseOutcome
// and dispatches ONE outcome; the page shows ONE message from bulkCaseOutcomeMessage.

const CASE_TYPE_FRAUD = 'Fraud Only';
const CASE_TYPE_AML = 'AML Only';

const plural = (n, one, many) => (n === 1 ? one : many);

const MAX_NAMED_CASES = 3;
function casesSuffix(caseIds) {
  const ids = caseIds || [];
  if (!ids.length) return '';
  const shown = ids.slice(0, MAX_NAMED_CASES).join(', ');
  const more =
    ids.length > MAX_NAMED_CASES
      ? ` +${ids.length - MAX_NAMED_CASES} more`
      : '';
  return ` (${plural(ids.length, 'case', 'cases')} ${shown}${more})`;
}

/**
 * Fold per-customer request results into one outcome.
 *
 * results: [{ customerKey, alertIds, ok: true, res } | { customerKey, alertIds, ok: false, error }]
 *   create res: { caseId, skippedAlreadyCased, skippedUnknown }
 *   merge  res: { merged, skippedAlreadyCased, skippedUnknown }
 * A failure whose error carries `alreadyCased` is the BE's 409 ALERTS_ALREADY_CASED (EVERY alert of
 * that request was already in a case) — counted as skipped alerts, not as a failure.
 * extra.alreadyCased [{ alertId, caseId }]: alerts the page left out of the requests because they are
 * already in a case. They are skipped just the same, so they are counted the same way.
 */
export function buildBulkCaseOutcome(kind, results, extra = {}) {
  const { alreadyCased = [], ...rest } = extra;
  const outcome = {
    kind,
    ...rest,
    created: [],
    merged: 0,
    skippedAlreadyCased: 0,
    // FIN-13084 / FIN-13218 / FIN-13684 — the owning cases are named so the analyst can reach them.
    casedInCaseIds: [],
    skippedUnknown: 0,
    failed: []
  };
  // FIN-13933 — one alert can sit under several selected customers; it is skipped once.
  const seenCased = new Set();
  const addCased = (cased) => {
    cased.forEach((c) => {
      if (!c || seenCased.has(c.alertId)) return;
      seenCased.add(c.alertId);
      outcome.skippedAlreadyCased += 1;
      if (c.caseId != null && !outcome.casedInCaseIds.includes(c.caseId))
        outcome.casedInCaseIds.push(c.caseId);
    });
  };
  addCased(alreadyCased);
  (results || []).forEach((r) => {
    if (r.ok) {
      const res = r.res || {};
      if (kind === 'create') {
        outcome.created.push({
          customerKey: r.customerKey,
          caseId: res.caseId
        });
      } else {
        outcome.merged += (res.merged || []).length;
      }
      outcome.skippedAlreadyCased += (res.skippedAlreadyCased || []).length;
      outcome.skippedUnknown += (res.skippedUnknown || []).length;
      return;
    }
    const cased = r.error && r.error.alreadyCased;
    if (Array.isArray(cased) && cased.length > 0) {
      addCased(cased);
    } else {
      // Prefer the BE's typed error body (e.g. "Case 999999 not found.") over axios's generic text —
      // same preference as the CM merge saga (FIN-12953).
      const e = r.error || {};
      outcome.failed.push({
        customerKey: r.customerKey,
        message:
          (e.response && e.response.data && e.response.data.error) ||
          e.message ||
          null
      });
    }
  });
  return outcome;
}

function failureSuffix(failed, fallback) {
  if (!failed.length) return '';
  const first = failed.find((f) => f.message);
  const reason = first ? first.message : fallback;
  return `${failed.length} ${plural(
    failed.length,
    'customer',
    'customers'
  )} failed: ${reason}`;
}

function createSuccessText(n, caseType) {
  // FIN-11360 (A1) per-mode wording, now with the ACTUAL number of cases created.
  if (caseType === CASE_TYPE_FRAUD)
    return `${n} case(s) created for the customers Fraud alerts`;
  if (caseType === CASE_TYPE_AML)
    return `${n} case(s) created for the customers AML alerts`;
  return `${n} combined FRAML case(s) created, one per customer`;
}

/** One toast for the whole action: { level: 'success' | 'warning' | 'error', text }. */
export function bulkCaseOutcomeMessage(outcome) {
  const {
    kind,
    caseType,
    created = [],
    merged = 0,
    skippedAlreadyCased: cased = 0,
    casedInCaseIds = [],
    skippedUnknown: unknown = 0,
    failed = []
  } = outcome || {};

  const skipParts = [];
  if (cased > 0)
    skipParts.push(
      `${cased} already part of a case${casesSuffix(casedInCaseIds)}`
    );
  if (unknown > 0) skipParts.push(`${unknown} no longer available`);
  const failText = failureSuffix(
    failed,
    kind === 'create' ? 'Failed to create case.' : 'Failed to merge alerts.'
  );

  if (kind === 'create') {
    const n = created.length;
    if (n === 0) {
      if (cased > 0 || unknown > 0) {
        const text = [
          `No case created — ${cased + unknown} selected ${plural(
            cased + unknown,
            'alert',
            'alerts'
          )} skipped (${skipParts.join(', ')})`,
          failText
        ]
          .filter(Boolean)
          .join('; ');
        return { level: 'error', text: `${text}.` };
      }
      return {
        level: 'error',
        text: failed.length
          ? `No case created; ${failText}`
          : 'No case created.'
      };
    }
    if (!skipParts.length && !failed.length) {
      return { level: 'success', text: createSuccessText(n, caseType) };
    }
    const text = [
      `${n} case(s) created`,
      skipParts.length ? `skipped: ${skipParts.join(', ')}` : null,
      failText || null
    ]
      .filter(Boolean)
      .join('; ');
    return { level: 'warning', text: `${text}.` };
  }

  // merge
  if (merged === 0) {
    if (cased > 0 || unknown > 0) {
      const text = [
        `Nothing merged — ${cased + unknown} selected ${plural(
          cased + unknown,
          'alert',
          'alerts'
        )} skipped (${skipParts.join(', ')})`,
        failText
      ]
        .filter(Boolean)
        .join('; ');
      return { level: 'error', text: `${text}.` };
    }
    const first = failed.find((f) => f.message);
    return {
      level: 'error',
      text: first ? first.message : 'Failed to merge alerts.'
    };
  }
  if (!skipParts.length && !failed.length) {
    return { level: 'success', text: 'Alerts merged successfully' };
  }
  if (cased > 0 && unknown === 0 && !failed.length) {
    // Unchanged FIN-12898 wording — QA verified this exact string.
    return {
      level: 'warning',
      text: `${merged} alert(s) merged; ${cased} skipped (already part of a case).`
    };
  }
  const text = [
    `${merged} alert(s) merged`,
    skipParts.length ? `skipped: ${skipParts.join(', ')}` : null,
    failText || null
  ]
    .filter(Boolean)
    .join('; ');
  return { level: 'warning', text: `${text}.` };
}
